import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { IMAGES } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const COLLECTIONS = [
    {
        id: "laptop",
        no: "01",
        series: "The Meridian Collection",
        title: "Leather Laptop Bags",
        sub: "Men & Women",
        blurb: "Padded 15.6\u2033 compartments, full-grain & NDM leather, solid brass fittings, custom embossing.",
        img: "/assets/laptop-2.png",
        cls: "md:col-span-8 min-h-[420px]",
        category: "Leather Laptop Bags",
        page: "leather-bags",
    },
    {
        id: "wallets",
        no: "02",
        series: "The Statesman Series",
        title: "Men's Wallets",
        sub: "Bifolds & Cardholders",
        blurb: "Slim RFID-ready bifolds, money clips and cardholders in drum-dyed leather.",
        img: IMAGES.wallets,
        cls: "md:col-span-4 min-h-[420px]",
        category: "Men's Wallets",
        page: "wallets",
    },
    {
        id: "sling",
        no: "03",
        series: "The Voyager Line",
        title: "Leather Sling Bags",
        sub: "Crossbody & Travel",
        blurb: "Compact crossbody slings with adjustable straps and antique hardware.",
        img: "/assets/bag-sling.png",
        cls: "md:col-span-4 min-h-[420px]",
        category: "Leather Sling Bags",
        page: "leather-bags",
    },
    {
        id: "travel",
        no: "04",
        series: "The Overlander Range",
        title: "Travel & Duffle Bags",
        sub: "Backpacks & Weekenders",
        blurb: "Full-grain duffles, rucksacks and utility backpacks — built for journeys, finished for your label.",
        img: "/assets/bag-duffle.png",
        cls: "md:col-span-8 min-h-[420px]",
        category: "Travel & Duffle Bags",
        page: "leather-bags",
    },
    {
        id: "candles",
        no: "05",
        series: "The Ember Atelier",
        title: "Handmade Candles",
        sub: "Botanical & Scented",
        blurb: "Hand-poured soy and beeswax candles in bespoke vessels and signature scents.",
        img: "/assets/candle-1.png",
        cls: "md:col-span-8 min-h-[420px]",
        category: "Handmade Candles",
        page: "candles",
    },
    {
        id: "magnets",
        no: "06",
        series: "The Keepsake Studio",
        title: "Fridge Magnets",
        sub: "Souvenir & Resin",
        blurb: "Custom souvenir and resin magnets — city themes, private artwork, gift retail.",
        img: "/assets/magnet-2.png",
        cls: "md:col-span-4 min-h-[420px]",
        category: "Fridge Magnets",
        page: "magnets",
    },
];

const Card = ({ c, onEnquire }) => {
    const navigate = useNavigate();
    return (
    <motion.article
        data-testid={`collection-card-${c.id}`}
        onClick={() => navigate(`/collections/${c.page}`)}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className={`group relative overflow-hidden rounded-md border border-[#2E2620] hover:border-[#C58B49]/50 transition-colors duration-500 cursor-pointer ${c.cls}`}
    >
        <img
            src={c.img}
            alt={`${c.title} — ${c.series} by FnB International`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C08]/95 via-[#0F0C08]/35 to-[#0F0C08]/10 transition-opacity duration-500" />

        <div className="absolute top-5 left-5 font-mono2 text-[11px] tracking-[0.3em] text-[#D4A373]">
            {c.no}
        </div>

        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
            <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#C58B49] mb-2">
                {c.series}
            </p>
            <h3 className="font-display text-3xl sm:text-4xl font-semibold text-[#F4EFEA] leading-tight">
                {c.title}
            </h3>
            <p className="text-xs tracking-[0.18em] uppercase text-[#8C8075] mt-1">{c.sub}</p>
            <p className="mt-3 max-w-md text-sm text-[#C2B7AD] leading-relaxed opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                {c.blurb}
            </p>
            <button
                data-testid={`collection-enquire-${c.id}`}
                onClick={(e) => { e.stopPropagation(); onEnquire(c.category); }}
                className="mt-4 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#D4A373] hover:text-[#F4EFEA] transition-colors"
            >
                Enquire about this line
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
        </div>
    </motion.article>
    );
};

export const ProductBento = ({ onEnquire }) => (
    <section id="collections" data-testid="collections-section" className="relative py-24 sm:py-32 bg-[#0F0C08]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE }}
                className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
            >
                <div>
                    <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                        The Collections
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.05]">
                        Six lines.
                        <br />
                        One standard of <em className="italic text-[#D4A373]">finish</em>.
                    </h2>
                </div>
                <p className="max-w-sm text-sm text-[#8C8075] leading-relaxed">
                    Every collection is produced in-house and can be tailored to your market —
                    leather grade, hardware, scent, artwork, packaging and private label.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                {COLLECTIONS.map((c) => (
                    <Card key={c.id} c={c} onEnquire={onEnquire} />
                ))}
            </div>
        </div>
    </section>
);
